import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Grid, InputAdornment, MenuItem,
  Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography
} from '@mui/material';
import { ArrowBack, Edit, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';

const blankForm = {
  first_name: '', last_name: '', email: '', phone_number: '', street_address: '',
  city: '', state: '', zip_code: '', gender: '', date_of_birth: '', email_verified: false,
  grade: '', allergies: '', medical_conditions: '', additional_notes: '', waiver_signed: false
};

const roleNames = (user) => (user.roles || []).map((r) => r.name);

function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('all');
  const [verification, setVerification] = useState('all');
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const loadUsers = async () => {
    try { setLoading(true); setUsers((await api.getUsers()) || []); setError(''); }
    catch (e) { setError(e.response?.data?.error || 'Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, []);

  const roles = useMemo(() => [...new Set(users.flatMap(roleNames))].sort(), [users]);
  const filtered = useMemo(() => users.filter((u) => {
    const q = search.trim().toLowerCase();
    const searchable = `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''} ${u.phone_number || ''}`.toLowerCase();
    if (q && !searchable.includes(q)) return false;
    if (role !== 'all' && !roleNames(u).includes(role)) return false;
    if (verification === 'verified' && !u.email_verified) return false;
    if (verification === 'unverified' && u.email_verified) return false;
    return true;
  }), [users, search, role, verification]);

  const openUser = async (id) => {
    try {
      setDetailLoading(true); setDialogError('');
      const d = await api.getAdminUser(id);
      setDetail(d);
      const u = d.user;
      const cp = d.child_properties || {};
      setForm({
        first_name: u.first_name || '', last_name: u.last_name || '', email: u.email || '',
        phone_number: u.phone_number || '', street_address: u.street_address || '', city: u.city || '',
        state: u.state || '', zip_code: u.zip_code || '', gender: u.gender || '',
        date_of_birth: u.date_of_birth ? u.date_of_birth.split('T')[0] : '', email_verified: !!u.email_verified,
        grade: cp.grade ?? '', allergies: cp.allergies || '', medical_conditions: cp.medical_conditions || '',
        additional_notes: cp.additional_notes || '', waiver_signed: !!cp.waiver_signed
      });
    } catch (e) { setDialogError(e.response?.data?.error || 'Failed to load user'); }
    finally { setDetailLoading(false); }
  };

  const closeDialog = () => { setDetail(null); setDialogError(''); };
  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const save = async () => {
    if (!detail) return;
    try {
      setSaving(true); setDialogError('');
      const payload = {
        first_name: form.first_name, last_name: form.last_name, email: form.email,
        phone_number: form.phone_number, street_address: form.street_address,
        city: form.city, state: form.state, zip_code: form.zip_code,
        gender: form.gender, date_of_birth: form.date_of_birth, email_verified: form.email_verified
      };
      if (detail.child_properties) Object.assign(payload, {
        grade: Number(form.grade), allergies: form.allergies,
        medical_conditions: form.medical_conditions, additional_notes: form.additional_notes,
        waiver_signed: form.waiver_signed
      });
      const updated = await api.updateAdminUser(detail.user.id, payload);
      setDetail(updated);
      await loadUsers();
    } catch (e) { setDialogError(e.response?.data?.error || 'Failed to update user'); }
    finally { setSaving(false); }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
      <Container maxWidth="xl">
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin')} sx={{ mb: 2 }}>Back to Dashboard</Button>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>User Management</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>View and correct account, contact, verification, and child information.</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, or phone"
                InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
                <MenuItem value="all">All roles</MenuItem>{roles.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth label="Email status" value={verification} onChange={(e) => setVerification(e.target.value)}>
                <MenuItem value="all">All</MenuItem><MenuItem value="verified">Verified</MenuItem><MenuItem value="unverified">Unverified</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>
        {loading ? <Box textAlign="center" py={8}><CircularProgress /></Box> : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Name</strong></TableCell><TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Phone</strong></TableCell><TableCell><strong>Roles</strong></TableCell>
                <TableCell><strong>Verified</strong></TableCell><TableCell><strong>Joined</strong></TableCell><TableCell />
              </TableRow></TableHead>
              <TableBody>{filtered.map((u) => <TableRow key={u.id} hover>
                <TableCell>{u.first_name} {u.last_name}</TableCell><TableCell>{u.email || '—'}</TableCell>
                <TableCell>{u.phone_number || '—'}</TableCell>
                <TableCell>{roleNames(u).map((r) => <Chip key={r} label={r} size="small" sx={{ mr: .5, mb: .5 }} />)}</TableCell>
                <TableCell><Chip size="small" label={u.email_verified ? 'Verified' : 'Unverified'} color={u.email_verified ? 'success' : 'warning'} /></TableCell>
                <TableCell>{formatDate(u.created_at)}</TableCell>
                <TableCell><Button size="small" startIcon={<Edit />} onClick={() => openUser(u.id)}>View / Edit</Button></TableCell>
              </TableRow>)}</TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ p: 2 }}>Showing {filtered.length} of {users.length} users</Typography>
          </TableContainer>
        )}
      </Container>

      <Dialog open={!!detail || detailLoading} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? <Box textAlign="center" py={6}><CircularProgress /></Box> : detail && <>
            {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
            <Box sx={{ mb: 2 }}>{roleNames(detail.user).map((r) => <Chip key={r} label={r} sx={{ mr: 1 }} />)}</Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="First Name" name="first_name" value={form.first_name} onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Last Name" name="last_name" value={form.last_name} onChange={change} /></Grid>
              <Grid item xs={12} sm={8}><TextField fullWidth label="Email" name="email" value={form.email} onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><FormControlLabel control={<Switch name="email_verified" checked={form.email_verified} onChange={change} />} label="Email Verified" /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" name="phone_number" value={form.phone_number} onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Date of Birth" name="date_of_birth" value={form.date_of_birth} onChange={change} InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField select fullWidth label="Gender" name="gender" value={form.gender} onChange={change}><MenuItem value="">Not set</MenuItem><MenuItem value="Male">Male</MenuItem><MenuItem value="Female">Female</MenuItem></TextField></Grid>
              <Grid item xs={12}><TextField fullWidth label="Street Address" name="street_address" value={form.street_address} onChange={change} /></Grid>
              <Grid item xs={12} sm={5}><TextField fullWidth label="City" name="city" value={form.city} onChange={change} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="State" name="state" value={form.state} onChange={change} /></Grid>
              <Grid item xs={6} sm={4}><TextField fullWidth label="ZIP Code" name="zip_code" value={form.zip_code} onChange={change} /></Grid>
              {detail.child_properties && <>
                <Grid item xs={12}><Typography variant="h6" sx={{ mt: 2 }}>Child / Medical Information</Typography></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth type="number" label="Grade" name="grade" value={form.grade} onChange={change} inputProps={{ min: 1, max: 12 }} /></Grid>
                <Grid item xs={12} sm={8}><FormControlLabel control={<Switch name="waiver_signed" checked={form.waiver_signed} onChange={change} />} label="Waiver Signed" /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Allergies" name="allergies" value={form.allergies} onChange={change} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Medical Conditions" name="medical_conditions" value={form.medical_conditions} onChange={change} /></Grid>
                <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Additional Notes" name="additional_notes" value={form.additional_notes} onChange={change} /></Grid>
              </>}
            </Grid>
            {detail.children?.length > 0 && <Box sx={{ mt: 3 }}><Typography variant="h6">Children</Typography>{detail.children.map((c) => <Button key={c.id} onClick={() => openUser(c.id)}>{c.first_name} {c.last_name}</Button>)}</Box>}
            <Box sx={{ mt: 3 }}><Typography variant="body2" color="text.secondary">
              Enrollments made: {detail.enrollments_made?.length || 0} · Enrollments as participant: {detail.enrollments_as_enrollee?.length || 0}
            </Typography></Box>
          </>}
        </DialogContent>
        <DialogActions><Button onClick={closeDialog}>Close</Button><Button variant="contained" onClick={save} disabled={saving || !detail}>{saving ? 'Saving…' : 'Save Changes'}</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminUsers;
